<template>
  <main>
    <md-card>
      <md-card-header>
        <div class="md-title">ログ管理</div>
      </md-card-header>

      <loading-spinner v-if="loadingSpinner"></loading-spinner>
      <md-card-content v-else>
        <div class="md-layout md-gutter">
          <div class="md-layout-item md-small-size-100">
            <md-autocomplete :class="getValidationClass('selectCorporateName', 'corporation_id')"
                             v-model="form.selectCorporateName"
                             :md-options="corporationOptions" :disabled="sending"
                             @md-changed="getCorporationOptions" @md-opened="getCorporationOptions">
              <label>法人名</label>

              <template slot="md-autocomplete-item" slot-scope="{item}">{{ item.name }}</template>
              <span class="md-error"
                    v-if="getValidationResponseMessage('corporation_id')">{{
                  getValidationResponseMessage('corporation_id')
                }}</span>
              <span class="md-error"
                    v-else-if="!$v.form.selectCorporateName.corporation_id.required">{{
                  errors.selectCorporateName.corporation_id.required
                }}</span>
              <span class="md-error"
                    v-else-if="!$v.form.selectCorporateName.corporation_id.numeric">{{
                  errors.selectCorporateName.corporation_id.numeric
                }}</span>
            </md-autocomplete>
          </div>
          <div class="md-layout-item md-small-size-100" v-if="loadingGroups">
            <md-field>
              <label>部門</label>
              <loading-spinner-overlay></loading-spinner-overlay>
            </md-field>
          </div>
          <div class="md-layout-item md-small-size-100" v-else>
            <md-field :class="getValidationClass('group_id')">
              <label>部門</label>
              <md-select name="selectDepartment" id="selectDepartment"
                         :disabled="sending || !this.selectedDepartments.length"
                         v-model="form.group_id">
                <md-option v-for="department in selectedDepartments" v-bind:key="'group-' + department.id"
                           :value="department.id">
                  {{ department.name }}
                </md-option>
              </md-select>
              <span class="md-error"
                    v-if="getValidationResponseMessage('group_id')">{{
                  getValidationResponseMessage('group_id')
                }}</span>
              <span class="md-error"
                    v-else-if="!$v.form.group_id.numeric">{{ errors.group_id.numeric }}</span>
            </md-field>
          </div>
          <div class="md-layout-item md-small-size-100">
            <md-field :class="getValidationClass('log_type')">
              <label>ログの種類</label>
              <md-select name="selectLogs" id="selectLogs" v-model="form.log_type" :disabled="sending">
                <md-option v-for="(log,index) in logs" v-bind:key="index" :value="index">
                  {{ log }}
                </md-option>
              </md-select>
              <span class="md-error"
                    v-if="getValidationResponseMessage('log_type')">{{
                  getValidationResponseMessage('log_type')
                }}</span>
              <span class="md-error"
                    v-else-if="!$v.form.log_type.required">{{ errors.log_type.required }}</span>
              <span class="md-error"
                    v-else-if="!$v.form.log_type.numeric">{{ errors.log_type.numeric }}</span>
            </md-field>
          </div>
        </div>

        <div class="md-layout md-gutter">
          <div class="md-layout-item md-small-size-100" v-if="loadingAccounts">
            <md-field>
              <label>取得者</label>
              <loading-spinner-overlay></loading-spinner-overlay>
            </md-field>
          </div>
          <div class="md-layout-item md-small-size-100" v-else>
            <md-autocomplete :disabled="!form.group_id || sending || loadingAccounts"
                             :md-options="getAccounts"
                             v-model="form.account">
              <label>取得者</label>

              <template slot="md-autocomplete-item" slot-scope="{ item, term }">
                <md-highlight-text :md-term="term">{{ item }}</md-highlight-text>
              </template>

              <template slot="md-autocomplete-empty" slot-scope="{ term }">
                {{ noDataMessage }}
              </template>
            </md-autocomplete>
          </div>
          <div class="md-layout-item md-small-size-100"></div>
          <div class="md-layout-item md-small-size-100"></div>
        </div>
        <div class="md-layout md-gutter">
          <label class="md-layout md-layout-item md-alignment-center-left md-size-10 md-small-size-100">
            期間
          </label>
          <div class="md-layout md-layout-item md-gutter md-alignment-center md-size-90 md-small-size-100">
            <md-field :class="getValidationClass('start_date')"
                      class="md-layout-item md-size-20 md-small-size-100">
              <date-picker :disabled-dates="calendarState.disableFrom" v-model="form.start_date"></date-picker>
              <span class="md-error"
                    v-if="getValidationResponseMessage('start_date')">{{
                  getValidationResponseMessage('start_date')
                }}</span>
              <span class="md-error"
                    v-else-if="!$v.form.start_date.required">{{ errors.start_date.required }}</span>
            </md-field>
            <label class="md-alignment-center-left">〜</label>
            <md-field :class="getValidationClass('end_date')"
                      class="md-layout-item md-size-20 md-small-size-100">
              <date-picker :disabled-dates="calendarState.disableTo" v-model="form.end_date"></date-picker>
              <span class="md-error"
                    v-if="getValidationResponseMessage('end_date')">{{
                  getValidationResponseMessage('end_date')
                }}</span>
              <span class="md-error"
                    v-else-if="!$v.form.end_date.required">{{ errors.end_date.required }}</span>
            </md-field>
            <div class="md-layout md-layout-item md-alignment-center-left">
              <md-button @click="validateSearchSubmit" type="submit" class="md-primary md-raised" :disabled="sending">
                <md-icon>search</md-icon>
                検索
              </md-button>
              <md-button @click="exportCsv" type="submit" class="md-primary md-raised" :disabled="sending">
                <md-icon>save_alt</md-icon>
                CSV出カ
              </md-button>
            </div>
          </div>
        </div>

        <loading-spinner v-if="loadingLogs"></loading-spinner>
        <md-table v-else-if="pagination.total !== null && isLoginHistory()" class="custom-table">
          <md-table-toolbar>
            <h1 class="md-title">一覧</h1>
          </md-table-toolbar>
          <md-table-row>
            <md-table-head><span>日時</span></md-table-head>
            <md-table-head><span>アカウント</span></md-table-head>
            <md-table-head><span>ログイン失敗理由</span></md-table-head>
          </md-table-row>
          <md-table-row v-if="histories.length === 0">
            <md-table-cell colspan="3">{{ emptyResponseMessage }}</md-table-cell>
          </md-table-row>
          <md-table-row v-else v-for="(history, index) in histories" v-bind:key="'history-key-' + index">
            <md-table-cell>{{ history.processed_at }}</md-table-cell>
            <md-table-cell>{{ history.account }}</md-table-cell>
            <md-table-cell>{{ history.failure_reason }}</md-table-cell>
          </md-table-row>
        </md-table>
        <md-table v-else-if="pagination.total !== null && !isRegistrationHistory()" class="custom-table">
          <md-table-toolbar>
            <h1 class="md-title">一覧</h1>
          </md-table-toolbar>
          <md-table-row>
            <md-table-head><span>日時</span></md-table-head>
            <md-table-head><span>アカウント</span></md-table-head>
            <md-table-head v-if="isLocationSearch()"><span>キーワード</span></md-table-head>
            <md-table-head><span>内容</span></md-table-head>
          </md-table-row>
          <md-table-row v-if="histories.length === 0">
            <md-table-cell v-if="isLocationSearch()" colspan="4">{{ emptyResponseMessage }}</md-table-cell>
            <md-table-cell v-else colspan="3">{{ emptyResponseMessage }}</md-table-cell>
          </md-table-row>
          <md-table-row v-else v-for="(history, index) in histories" v-bind:key="'history-key-' + index">
            <md-table-cell>{{ history.processed_at }}</md-table-cell>
            <md-table-cell>{{ history.account }}</md-table-cell>
            <md-table-cell v-if="isLocationSearch()">{{ history.keyword }}</md-table-cell>
            <md-table-cell>
              <a :href="'https://www.google.co.jp/maps/@' + history.content + ',17z?hl=ja'"
                 rel="noopener"
                 target="_blank" v-if="history.content">
                {{ history.content }}
              </a>
            </md-table-cell>
          </md-table-row>
        </md-table>
        <md-table v-else-if="pagination.total !== null && isRegistrationHistory()" class="custom-table">
          <md-table-toolbar>
            <h1 class="md-title">一覧</h1>
          </md-table-toolbar>
          <md-table-row>
            <md-table-head><span>取得⽇時</span></md-table-head>
            <md-table-head><span>種類</span></md-table-head>
            <md-table-head><span>種別</span></md-table-head>
            <md-table-head><span>所在、地番／家屋番号</span></md-table-head>
            <md-table-head><span>ユーザーID</span></md-table-head>
            <md-table-head><span>取得者</span></md-table-head>
            <md-table-head><span>取得結果</span></md-table-head>
            <md-table-head><span></span></md-table-head>
          </md-table-row>
          <md-table-row v-if="histories.length === 0">
            <md-table-cell colspan="8">{{ emptyResponseMessage }}</md-table-cell>
          </md-table-row>
          <md-table-row v-else :key="'registration-' + index" v-for="(registration, index) in histories">
            <md-table-cell><span>{{ registration.requested_at }}</span></md-table-cell>
            <md-table-cell><span>{{ getPdfTypeValue(registration.pdf_type) }}</span></md-table-cell>
            <md-table-cell><span>{{ getNumberTypeValue(registration.number_type) }}</span></md-table-cell>
            <md-table-cell><span>{{ registration.label }}</span></md-table-cell>
            <md-table-cell><span>{{ getAccountById(registration.account_id).username }}</span></md-table-cell>
            <md-table-cell><span>{{ getAccountById(registration.account_id).name }}</span></md-table-cell>
            <md-table-cell>
              <a v-if="registration.s3_object_url" :href="''" class="md-raised"
                 @click.prevent="downloadPdf(registration, form.selectCorporateName.corporation_id)">
                <md-icon class="material-icons">
                  picture_as_pdf
                </md-icon>
                <span>{{ registration.result }}</span>
              </a>
              <span v-else>{{ registration.result }}</span>
            </md-table-cell>
            <md-table-cell>
              <a :href="'https://www.google.co.jp/maps/@' + registration.content + ',17z?hl=ja'"
                 rel="noopener"
                 v-if="registration.content" target="_blank">
                {{ registration.content }}
              </a>
            </md-table-cell>
          </md-table-row>
        </md-table>
        <pagination v-if="!loadingLogs && pagination.total"
                    :total="pagination.total"
                    :per-page="pagination.per_page"
                    :current-page="pagination.current_page"
                    @pagechanged="onPageChanged"
        />
      </md-card-content>
    </md-card>
    <md-snackbar :md-active="true" :md-position="'center'" class="md-snackbar-bg__error"
                 v-if="errorMessages.length">
      <flash-message :errors="errorMessages" :inSnackbar="true"></flash-message>
      <md-button class="md-primary" @click="errorMessages = []">閉じる</md-button>
    </md-snackbar>
  </main>
</template>

<script>
import moment                                            from 'moment';
import {validationMixin}                                 from 'vuelidate'
import {numeric, required,}                              from 'vuelidate/lib/validators'
import DatePicker                                        from "../../../components/DatePicker";
import LoadingSpinner                                    from '../../../components/LoadingSpinner';
import Pagination                                        from '../../../components/Pagination';
import {apiClientWithManagerToken as apiClient, apiList} from '../../../modules/apiClient';
import Common                                            from '../../../modules/common';
import Validation                                        from "../../../modules/validation";
import CommonMixin                                       from "../../../mixins/commonMixin";
import LoadingSpinnerOverlay                             from "../../../components/LoadingSpinnerOverlay";
import FlashMessage                                      from "../../../components/FlashMessage";

const WITH_TRASHED = 1;
export default {
  name      : "AdminLogs",
  mixins    : [validationMixin, CommonMixin],
  components: {
    LoadingSpinnerOverlay,
    DatePicker,
    LoadingSpinner,
    Pagination,
    FlashMessage,
  },
  data      : () => ({
    sending             : false,
    loadingSpinner      : false,
    loadingLogs         : false,
    loadingGroups       : false,
    loadingAccounts     : false,
    form                : {
      selectCorporateName: null,
      group_id           : null,
      account            : null,
      log_type           : null,
      start_date         : moment().format(Common.momentConst.dateFormat),
      end_date           : moment().format(Common.momentConst.dateFormat),
    },
    calendarState       : {
      disableFrom: {
        to  : moment().subtract(3, 'months').subtract(1, 'days').toDate(),
        from: moment().hour(23).minute(59).second(59).toDate(), // 今日の23:59:59まで
      },
      disableTo  : {
        to  : null,
        from: moment().hour(23).minute(59).second(59).toDate(), // 今日の23:59:59まで
      }
    },
    pagination          : {
      total       : null,
      per_page    : Common.limitPageDefault,
      current_page: null,
    },
    selectedCorporations: [],
    selectedDepartments : [],
    accounts            : [],
    logs                : Common.typeLogs,
    pdfTypes            : Common.pdfTypes,
    numberTypes         : Common.numberTypes,
    histories           : [],
    errorMessages       : [],
    emptyResponseMessage: Common.messages.errorGet,
    validateResponse    : [],
    errors              : {
      selectCorporateName: {
        corporation_id: {
          required: Validation.errorMessage(Validation.rules.required, 'corporationName'),
          numeric : Validation.errorMessage(Validation.rules.numeric, 'corporationName'),
        },
      },
      group_id           : {
        numeric: Validation.errorMessage(Validation.rules.numeric, 'groupName'),
      },
      log_type           : {
        required: Validation.errorMessage(Validation.rules.required, 'logType'),
        numeric : Validation.errorMessage(Validation.rules.numeric, 'logType'),
      },
      start_date         : {
        required: Validation.errorMessage(Validation.rules.required, 'startLogDate'),
      },
      end_date           : {
        required: Validation.errorMessage(Validation.rules.required, 'endLogDate'),
      },
    },
    searchLogType       : null,
    corporationOptions  : [],
  }),
  validations() {
    return {
      form: {
        selectCorporateName: {
          corporation_id: {
            required,
            numeric,
          },
        },
        group_id           : {
          numeric,
        },
        log_type           : {
          required,
          numeric,
        },
        start_date         : {
          required,
        },
        end_date           : {
          required,
        },
      },
    };
  },
  updated : function () {
    this.calendarState.disableTo.to = moment(this.form.start_date).toDate();
  },
  computed: {
    getAccounts() {
      return this.parseAccounts();
    },
  },
  methods : {
    /**
     * 法人リストボックスの内容をキーワードで更新する
     *
     * @param searchTerm
     */
    getCorporationOptions(searchTerm) {
      this.corporationOptions = new Promise(resolve => {
        window.setTimeout(() => {
          if (!searchTerm) {
            resolve(this.parseCorporations());
            return;
          }

          const keyword = typeof (searchTerm) === 'object' ? searchTerm.uid : searchTerm;
          apiClient.get(apiList.jon.corporations.corporations, {params: {keyword: keyword}})
              .then(response => {
                if (this.hasResponseErrors(response)) {
                  return;
                }

                const term                = searchTerm.toLowerCase()
                this.selectedCorporations = response.data.corporations;
                return resolve(this.parseCorporations().filter(({name}) => name.toLowerCase().includes(term)))
              })
              .catch(error => {
                this.errorMessages = [error.message];
              });
        }, 500)
      })
    },
    parseCorporations() {
      return this.selectedCorporations.map(corporation => ({
        'corporation_id': corporation.corporation_id,
        'name'          : corporation.uid + '：' + corporation.name,
        'toLowerCase'   : () => corporation.uid + '：' + corporation.name.toLowerCase(),
        'toString'      : () => corporation.uid + '：' + corporation.name,
        'uid'           : corporation.uid,
      }));
    },
    parseAccounts() {
      const groupId = this.form.group_id;
      if (!groupId) {
        return [];
      }
      return this.accounts.filter(account => account.group_id === groupId).map(account => ({
        'id'         : account.account_id,
        'name'       : account.account_id + ':' + account.name,
        'username'   : account.username,
        'toLowerCase': () => account.account_id + ':' + account.name.toLowerCase(),
        'toString'   : () => account.account_id + ':' + account.name,
      }));
    },
    getMinDate() {
      return moment().subtract(3, 'months').format(Common.momentConst.dateFormat);
    },
    getMaxDate() {
      return moment().format(Common.momentConst.dateFormat);
    },
    getMaxStartDate() {
      return this.form.end_date;
    },
    getMinFinishDate() {
      return this.form.start_date;
    },
    setAccounts() {
      const corporationId = this.form.selectCorporateName.corporation_id;
      const url           = apiList.jon.accounts.search;
      apiClient
          .get(url, {
            params: {
              corporation_id: corporationId,
              with_trashed  : WITH_TRASHED,
            },
          })
          .then(response => {
            const result  = response.data;
            this.accounts = result.accounts;
          })
          .catch(error => {
            this.accounts = [];
          })
          .finally(() => {
            this.loadingAccounts = false;
          });
    },
    setDepartments(corporationId) {
      this.loadingGroups = true;
      const url          = apiList.jon.corporations.groups.list.replace(':corporation_id', corporationId);
      apiClient.get(url)
          .then(response => {
            if (this.hasResponseErrors(response)) {
              return;
            }
            if (this.form.selectCorporateName.corporation_id) {
              const result             = response.data;
              this.selectedDepartments = result.groups;
            }
          })
          .catch(error => {
            this.errorMessages = [error.message];
          })
          .finally(() => {
            this.loadingGroups = false;
          });
    },
    setMetaPagination(meta) {
      this.pagination.total        = meta.total;
      this.pagination.per_page     = meta.per_page;
      this.pagination.current_page = meta.current_page;
    },
    parseHistories(histories) {
      return histories.map(function (history) {
        history.processed_at = moment(history.processed_at).format('YYYY-MM-DD HH:mm')
        return history;
      });
    },
    parseRegistrationHistories(histories) {
      return histories.map(function (history) {
        history.processed_at = moment(history.processed_at).format('YYYY-MM-DD HH:mm')
        history.content      = history.latitude + ',' + history.longitude
        if (!history.result || history.result.length === 0) {
          history.result = history.s3_object_url ? '取得' : '未取得';
        }
        return history;
      });
    },
    searchSubmit() {
      this.histories     = [];
      this.sending       = true;
      this.loadingLogs   = true;
      this.searchLogType = this.form.log_type;
      apiClient.get(apiList.history.logs, {
        params: {
          page              : this.pagination.current_page,
          limit             : this.pagination.per_page,
          corporation_id    : this.form.selectCorporateName.corporation_id,
          group_id          : this.form.group_id,
          account_id        : this.form.account ? this.form.account.id : null,
          log_type          : this.form.log_type,
          processed_at_begin: moment(this.form.start_date).format(Common.momentConst.dateFormat) + ' 00:00:00',
          processed_at_end  : moment(this.form.end_date).format(Common.momentConst.dateFormat) + ' 23:59:59',
        }
      })
          .then(response => {
            if (this.hasResponseErrors(response)) {
              return;
            }
            const result = response.data;
            if (result.histories) {
              this.histories = this.parseHistories(result.histories);
            } else if (result.data) {
              this.histories = this.parseRegistrationHistories(result.data);
            }
            this.setMetaPagination(result.meta)
          })
          .catch(error => {
            this.errorMessages = [error.message];
          })
          .finally(() => {
            this.sending     = false;
            this.loadingLogs = false;
            this.$v.$reset();
          });
    },
    clearForm() {
      this.$v.$touch();
      this.errorMessages    = [];
      this.validateResponse = [];
    },
    validateSearchSubmit() {
      this.clearForm();
      if (!this.$v.$invalid) {
        this.pagination.current_page = 1;
        this.searchSubmit()
      }
    },
    onPageChanged(page) {
      this.pagination.current_page = page;
      this.searchSubmit();
    },
    isLoginHistory() {
      return this.searchLogType === Common.loginLogType;
    },
    isRegistrationHistory() {
      for (let key of Object.keys(Common.registrationLogs)) {
        if (key === this.searchLogType) {
          return true;
        }
      }
      return false;
    },
    getNumberTypeValue(numberType) {
      let numberTypeName = '';
      this.numberTypes.forEach(numberTypeItem => {
        if (numberType === numberTypeItem.code) {
          numberTypeName = numberTypeItem.name;
        }
      });
      return numberTypeName;
    },
    getPdfTypeValue(pdfType) {
      let pdfName = '';
      this.pdfTypes.forEach(pdfTypeItem => {
        if (pdfType === pdfTypeItem.code) {
          pdfName = pdfTypeItem.name;
        }
      });
      return pdfName;
    },
    getAccountById(accountId) {
      const accounts = this.accounts.filter(account => account.account_id === accountId)
      if (accounts.length > 0) {
        return accounts[0];
      }
      return {username: null, name: null};
    },
    exportCsv() {
      this.sending = true;
      apiClient
          .post(apiList.history.report, {
            processed_at_begin: moment(this.form.start_date).format(Common.momentConst.dateFormat) + ' 00:00:00',
            processed_at_end  : moment(this.form.end_date).format(Common.momentConst.dateFormat) + ' 23:59:59',
            corporation_id    : this.form.selectCorporateName.corporation_id,
          })
          .then(response => {
            if (response.data.errors) {
              this.errorMessages = [Common.messages.history.downloadError];
              this.sending       = false;
              return;
            }
            this.downloadFile(response.data.path);
          })
          .catch(error => {
            this.errorMessages = [Common.messages.history.downloadError];
            this.sending       = false;
          })
          .finally(() => {
          });
    },
    downloadFile(path) {
      this.sending = true;
      apiClient
          .request({
            url         : apiList.history.download,
            method      : 'post',
            responseType: 'arraybuffer',
            data        : {
              path: path,
            },
          })
          .then(response => {
            if (response.data) {
              const blob = new Blob([response.data], {type: 'application/csv'})
              saveAs(blob, path);
            } else {
              this.errorMessages = [Common.messages.billings.downloadError];
            }
          })
          .catch(error => {
            this.errorMessages = [Common.messages.billings.downloadError];
          })
          .finally(() => {
            this.sending = false;
          });
    },
    isLocationSearch() {
      return this.searchLogType === Common.jonSearchLogType || this.searchLogType === Common.googleSearchLogType;
    },
  },
  watch   : {
    "form.selectCorporateName": function (val) {
      if (val.corporation_id) {
        this.setDepartments(val.corporation_id);
        this.setAccounts();
      } else {
        this.form.group_id            = null;
        this.form.selectedDepartments = [];
        this.form.account             = null;
        this.accounts                 = [];
      }
    },
  },
}
</script>

<style lang="scss">
@import "src/assets/scss/components.scss";

.md-table-cell-container {
  padding: 6px 12px 6px 12px;
}
</style>
